/**
 * A button with a popup list under it: the language and the user menu in the top bar.
 *
 * The list is a native popover (`popover="auto"`) that the button opens through `popovertarget`: the browser puts it in
 * the top layer, closes it on a click outside it and on Esc, and gives the focus back to where it was. CSS anchor
 * positioning puts it under the button (`.menu` in `assets/scss/base.scss`).
 *
 * While it is open a `<ShortcutLayer>` lies over the page (see `hooks/use-shortcuts.tsx`), so the page's keys do not go
 * off underneath it: the arrow keys, Home and End move between the items, Enter picks one (it is a button), Esc and
 * the menu's own function key close it.
 *
 *     <Menu label={user.name} shortcut="F7">
 *         <MenuText>{t('topNav.language')}</MenuText>
 *         <MenuItem checked={...} onSelect={() => pickLanguage('en')} lang="en">English</MenuItem>
 *     </Menu>
 */
import {useId, useRef, useState} from 'react';

import {CheckIcon, ChevronIcon} from '../partials/icons';
import {ShortcutLayer, useShortcuts} from '../../../hooks/use-shortcuts';

interface props {
    /** what the button shows */
    label: React.ReactNode;
    /** the accessible name of the button, when its label does not say what it opens */
    'aria-label'?: string;
    /** the function key that opens and closes the menu (`'F7'`), shown on the button */
    shortcut?: string;
    /** the classes of the button */
    className?: string;
    /** the items: `MenuItem`, `MenuText` */
    children: React.ReactNode;
}

/** The buttons of the menu that can get the focus. */
const itemsOf = (menu: HTMLElement): HTMLButtonElement[] => [
    ...menu.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'),
];

const close = (menu: HTMLElement | null): void => {
    if (menu?.matches(':popover-open')) {
        menu.hidePopover();
    }
};

/** The keys of an open menu, on the layer that lies over the page while it is open. */
const MenuKeys = ({menuRef, shortcut}: {menuRef: React.RefObject<HTMLElement | null>; shortcut?: string}): null => {
    const move = (to: (index: number, count: number) => number): void => {
        const items = menuRef.current ? itemsOf(menuRef.current) : [];
        const index = items.indexOf(document.activeElement as HTMLButtonElement);

        items[to(index, items.length)]?.focus();
    };

    useShortcuts({
        ArrowDown: () => move((index, count) => (index + 1) % count),
        ArrowUp: () => move((index, count) => (0 >= index ? count - 1 : index - 1)),
        Home: () => move(() => 0),
        End: () => move((_index, count) => count - 1),
        Escape: () => close(menuRef.current),
        ...(shortcut ? {[shortcut]: () => close(menuRef.current)} : {}),
    });

    return null;
};

export const Menu = ({label, 'aria-label': ariaLabel, shortcut, className, children}: props): React.JSX.Element => {
    const id = useId();
    // the button is the menu's anchor: a dashed ident, unique per menu
    const anchor = `--menu-${id.replace(/[^\w-]/g, '')}`;
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLMenuElement>(null);
    const [open, setOpen] = useState(false);
    // opened with a click (not a key): the browser gives the button the focus back when it closes
    const clickedRef = useRef(false);

    useShortcuts(
        shortcut
            ? {
                  [shortcut]: () => {
                      clickedRef.current = false;
                      menuRef.current?.showPopover();
                  },
              }
            : {},
    );

    const handleToggle = (event: React.ToggleEvent<HTMLMenuElement>): void => {
        const opened = 'open' === event.newState;

        setOpen(opened);

        if (opened) {
            // the picked item, else the first one
            const items = itemsOf(event.currentTarget);

            (items.find(item => 'true' === item.getAttribute('aria-pressed')) ?? items[0])?.focus();
        } else if (clickedRef.current && document.activeElement === buttonRef.current) {
            // see `blurAfterPointer` in `helpers/focus.ts`: a scan's Enter must not open it again
            buttonRef.current?.blur();
        }
    };

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className={className ?? 'button button-small'}
                aria-label={ariaLabel}
                aria-expanded={open}
                aria-controls={id}
                popoverTarget={id}
                style={{anchorName: anchor}}
                onClick={event => {
                    clickedRef.current = 0 < event.detail;
                }}
            >
                {label}
                {shortcut && <kbd>{shortcut}</kbd>}
                <ChevronIcon />
            </button>
            <menu
                ref={menuRef}
                id={id}
                popover="auto"
                className="menu"
                style={{positionAnchor: anchor}}
                onToggle={handleToggle}
            >
                {children}
            </menu>
            {open && (
                <ShortcutLayer>
                    <MenuKeys menuRef={menuRef} shortcut={shortcut} />
                </ShortcutLayer>
            )}
        </>
    );
};

interface itemProps {
    /** for an item of a choice (a language): whether it is the picked one, it gets a check */
    checked?: boolean;
    onSelect(): void;
    /** the language of the item's text, for an item named in its own language */
    lang?: string;
    children: React.ReactNode;
}

/** An item of a `Menu`: picking it closes the menu. */
export const MenuItem = ({checked, onSelect, lang, children}: itemProps): React.JSX.Element => (
    <li>
        <button
            type="button"
            lang={lang}
            aria-pressed={checked}
            onClick={event => {
                close(event.currentTarget.closest<HTMLElement>('[popover]'));
                onSelect();
            }}
        >
            {checked && <CheckIcon />}
            {children}
        </button>
    </li>
);

/** A line of text in a `Menu`: a heading over its items, or with `note` the keys that work in it, under them. */
export const MenuText = ({note = false, children}: {note?: boolean; children: React.ReactNode}): React.JSX.Element => (
    <li className={note ? 'menu-text menu-note' : 'menu-text'}>{children}</li>
);
